<template>
  <div>
    <es-filter :defaultExpanded="true">
      <template #filter-options>
        <div class="columns multi-dropdown-columns">
          <div class="column is-2">
            <div class="field">
              <label class="label has-text-weight-bold">
                Batch ID :
              </label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="batchIdInput"
                  :aria-label="batchIdInput || 'Batch ID'"
                >
              </div>
            </div>
          </div>
          <div class="column is-2">
            <es-select
              api="submission-types"
              key-value="code"
              label="Submission Type : "
              ref="selSubmissionType"
              value-tpl="${description}"
              :clearOut="true"
              :multi="true"
              @update:values="submissionTypes = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              keyValue="id"
              label="Status :"
              ref="selStatuses"
              valueTpl="${description}"
              :clearOut="true"
              :multi="true"
              :pre-selected="statusInput"
              :set-values="statuses"
              @update:values="statusInput = $event"
            />
          </div>
        </div>
        <div class="columns multi-dropdown-columns">
          <div class="column is-2">
            <es-select
              api="districts"
              key-value="code"
              ref="selDistricts"
              search-min-length="2"
              :apiQueryParams="getAllDistricts"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`)} :`"
              :multi="true"
              :pre-selected="districtCodes"
              :search="true"
              :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
              @update:values="districts = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              api="schools"
              :key-value="['districtCode', 'code']"
              ref="selSchools"
              :api-query-params="getDistrictParams"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`)} :`"
              :multi="true"
              :pre-selected="schoolCodesPromises"
              :search="true"
              search-min-length="2"
              :sub-value-tpl-fn="(result) =>
                `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
              :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
              @update:values="schools = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              key-value="id"
              ref="selYears"
              value-tpl="${year}"
              :clearOut="true"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_YEAR`)} :`"
              :multi="true"
              :pre-selected="schoolYearInput"
              :set-values="pidSchoolYearsDesc"
              @update:values="schoolYearInput = $event"
            />
          </div>
          <div class="column is-4">
            <div class="field no-border">
              <label class="label has-text-weight-bold">
                Process Date Range :
              </label>
              <div class="control is-flex">
                <input
                  class="input"
                  type="date"
                  v-model="startDateInput"
                  :aria-label="startDateInput || 'Start Date Input'"
                >
                <span
                  class="is-flex flex-align--center"
                  style="padding: 0 0.5rem; font-size: 12px"
                >
                  to
                </span>
                <input
                  class="input"
                  type="date"
                  v-model="endDateInput"
                  :aria-label="endDateInput || 'End Date Input'"
                >
              </div>
            </div>
          </div>
          <div
            class="column has-padding-right"
            style="margin-top: auto"
          >
            <div class="buttons flex-justify--end">
              <button
                class="button button-link has-underline"
                @click="resetFilter"
              >
                Reset
              </button>
              <button
                class="button is-primary"
                @click="filterData"
                :disabled="loadingBatches || (isStateUser && !schoolYearInput.length)"
                :title="isStateUser && !schoolYearInput.length ? 'School year is required.' : null"
              >
                Filter Data
              </button>
            </div>
          </div>
        </div>
      </template>
    </es-filter>
    <div style="padding: 0 1.25rem .5rem 2rem">
      <es-exception
        v-if="isStateUser && !schoolYear.length"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="School Year must be entered to view results."
      />
      <pid-batches-table
        v-else
        :batches="batches"
        :columnHeaders="columnHeaders"
        :loading="loadingBatches"
        :loadingError="loadingBatchError"
        :totalCount="batchesTotalCount"
        @updateSorter="updateSorter"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import PidBatchesTable from '@/components/PidBatchesTable.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    DateUtils,
    FieldLabels,
  ],
  components: {
    EsException,
    EsFilter,
    EsSelect,
    PidBatchesTable,
  },
  props: {
    batchId: {
      type: String,
    },
    districtCodes: {
      type: Array,
    },
    endDate: {
      type: String,
    },
    page: {
      type: String,
    },
    schoolCodes: {
      type: Array,
    },
    schoolYear: {
      type: Array,
    },
    status: {
      type: Array,
    },
    startDate: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    const STATUSES = [
      { status: 'COMPLETED', description: 'Completed', id: 1 },
      { status: 'ERRORED', description: 'Pending:  Validation Interrupted', id: 2 },
      { status: 'RUNNING_MATCHING', description: 'Pending: Assignment in Progress', id: 3 },
      { status: 'ERRORED_VALIDATION', description: 'Pending: Validation Errors', id: 4 },
      { status: 'ERRORED_MATCHING', description: 'Pending: Matching Errors', id: 5 },
      { status: 'COMPLETED_WITH_PENDING', description: 'Pending: Near Matches', id: 6 },
      // TODO: Add Near Match System Label
      { status: 'QUEUED', description: 'Pending: Request Queued', id: 7 },
      { status: 'LOADED', description: 'Pending: Request Loaded', id: 8 },
      { status: 'VALIDATION_IN_PROGRESS', description: 'Pending: Validation In Progress', id: 9 },
      { status: 'VALIDATION_COMPLETE', description: 'Pending: Validation Completed', id: 10 },
      { status: 'VALIDATION_CANCELED', description: 'Completed: Validation Canceled', id: 11 },
    ];
    return {
      // props and filter
      batchIdInput: this.batchId,
      districts: this.districtCodes.map(d => ({ code: d })),
      endDateInput: this.endDate,
      initialDate: null,
      schools: this.schoolCodes
        .map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: null,
      submissionTypes: [],
      startDateInput: this.startDate,
      statusInput: STATUSES.filter(s => this.status.find(status => status === s.status)),
      statuses: STATUSES,

      // data for batch view
      batches: [],
      batchesTotalCount: null,
      loadingBatches: false,
      loadingBatchError: null,
      userPersonTypesCodes: null,

      columnHeaders: [],
    };
  },
  async created() {
    this.setDataElements();
    if (this.schoolYear) {
      this.loadBatches();
    }
  },
  mounted() {
    this.$root.$on('refreshMatchHistory', this.loadBatches);
  },
  destroyed() {
    this.$root.$off('refreshMatchHistory');
  },
  watch: {
    $route() {
      if (!this.isStateUser || this.schoolYear.length) {
        this.loadBatches();
      }
    },
    districts() {
      this.$refs.selSchools.clear();
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      isStateUser: 'isStateUser',
    }),
    ...mapGetters('lookups', [
      'pidSchoolYearsDesc',
      'defaultPersonType',
    ]),
    dateDifference() {
      if (!this.initialDate) {
        return 29;
      }
      // Get today's date
      const today = new Date();
      // Calculate the time difference in milliseconds
      const timeDifference = today - this.initialDate;
      // Convert time difference from milliseconds to days
      const dayDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      // Return the difference in days
      return dayDifference;
    },
    getAllDistricts() {
      const params = {};
      params.active = 'all';
      return params;
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);
      params.active = 'all';
      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    hasInputParams() {
      return this.batchIdInput || this.districts.length || this.endDateInput || this.page
        || this.schools.length || this.startDateInput || this.schoolYearInput.length
        || this.statusInput.length || this.submissionTypes.length;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    schoolCodesPromises() {
      return this.fetchSchoolCodes();
    },
    searchParams() {
      const params = {};
      if (this.batchIdInput) {
        params.batchId = this.batchIdInput.trim();
      }
      if (this.submissionTypes) {
        params.submissionTypes = this.submissionTypes.map(c => c.code);
      }
      if (this.statusInput.length) {
        params.status = this.statusInput.map(s => s.status);
      }
      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }
      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }
      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }
      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.ids = this.batchId ? this.batchId : undefined;
      params.districtCodes = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCodes = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.statuses = this.status.length ? this.status : undefined;
      params.startDate = this.startDate ? this.startDate : undefined;
      params.submissionTypes = this.submissionTypes.length
        ? this.submissionTypes.map(c => c.code) : undefined;
      params.endDate = this.endDate ? this.endDate : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);

      return params;
    },
  },
  methods: {
    filterData() {
      if (!this.isStateUser || this.hasInputParams) {
        this.$router.replace({ query: this.searchParams });
      } else if (!this.hasInputParams && this.searchParams) {
        this.resetFilter();
      }
    },
    getBatches() {
      return axios.get('personid/batches',
        {
          params: this.urlParams,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    async fetchSchoolCodes() {
      const promises = [];
      this.schoolCodes.forEach(code => promises
        .push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
    async loadBatches() {
      try {
        this.$root.$emit('matchHistoryLoading', true);
        this.loadingBatches = true;
        this.loadingBatchError = false;
        this.batchesTotalCount = null;
        const response = await this.getBatches();
        this.batches = response.data.data;
        this.batchesTotalCount = response.data.totalCount;
      } catch (e) {
        this.loadingBatchError = true;
      } finally {
        this.$root.$emit('matchHistoryLoading', false);
        this.loadingBatches = false;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    resetFilter() {
      // TODO: searchParams() returns all keys (even if the value is undefined)
      if (Object.keys(this.urlParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selStatuses.clear();
      this.$refs.selYears.clear();
      this.$refs.selSubmissionType.clear();

      this.batchIdInput = '';
      this.startDateInput = '';
      this.endDateInput = '';

      this.batches = [];
      this.batchesTotalCount = null;

      this.$refs.selYears.pushValues(this.pidSchoolYearsDesc.filter(y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year));
      this.startDateInput = this.buildDateString(this.dateDifference);
      this.filterData();
    },
    setDataElements() {
      this.schoolYearInput = this.pidSchoolYearsDesc
        .filter(y => this.schoolYear.find(sy => sy === y.year));
      this.initialDate = this.$route.query.startDate ? new Date(this.$route.query.startDate) : null;
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.columnHeaders = [
        {
          header: null,
          field: null,
          style: {
            width: '55px',
          },
        },
        {
          header: 'Batch Info',
          title: 'Batch Info',
          field: 'id',
          order: 'DESC',
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Location Info',
          title: 'Location Info',
          field: 'districtCode',
          order: null,
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Process Info',
          title: 'Process Info',
          field: 'lastUpdateDate',
          order: null,
          sortable: true,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Record Info',
          title: 'Record Info',
          field: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Creation User',
          title: 'Creation User',
          field: 'createdBy',
          style: {
            width: '20%',
          },
          hideForBatches: true,
        },
        {
          header: 'Status Info',
          title: 'Status Info',
          field: 'status',
          order: null,
          sortable: true,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
          style: {
            width: '1%',
          },
        },
      ];
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
